/**
 * The slide text sizes, smallest first: the chips in the Learn toolbar's More menu and the stops of
 * the welcome slide's slider. The persisted `slideTextSize` device setting parses against this
 * list, so a value removed from it falls back to {@link DEFAULT_SLIDE_TEXT_SIZE}.
 */
export const SLIDE_TEXT_SIZES = ["small", "medium", "large", "extra-large"] as const;

export type SlideTextSize = (typeof SLIDE_TEXT_SIZES)[number];

export const DEFAULT_SLIDE_TEXT_SIZE = "medium" satisfies SlideTextSize;

/**
 * Each size as a multiplier of Tailwind's default type scale. `small` is the stock scale (a 16pt
 * paragraph); the default sits one step above it, since slides are prose read at arm's length.
 */
const SLIDE_TEXT_SIZE_SCALES = {
  "extra-large": 1.5,
  large: 1.3,
  medium: 1.15,
  small: 1,
} as const satisfies Record<SlideTextSize, number>;

const SLIDE_TEXT_SIZE_LABELS = {
  "extra-large": "Extra Large",
  large: "Large",
  medium: "Medium",
  small: "Small",
} as const satisfies Record<SlideTextSize, string>;

export function slideTextSizeLabel(size: SlideTextSize): string {
  return SLIDE_TEXT_SIZE_LABELS[size];
}

/**
 * Tailwind v4's default `--text-*` sizes in rem. Every `text-xs` … `text-9xl` utility reads its
 * font size from these variables (their `--text-*--line-height` companions are unitless ratios, so
 * line heights follow the font size on their own). Arbitrary sizes (`text-[17px]`) are inlined at
 * build time and deliberately stay fixed.
 */
const TAILWIND_TEXT_SIZES_REM = [
  ["--text-xs", 0.75],
  ["--text-sm", 0.875],
  ["--text-base", 1],
  ["--text-lg", 1.125],
  ["--text-xl", 1.25],
  ["--text-2xl", 1.5],
  ["--text-3xl", 1.875],
  ["--text-4xl", 2.25],
  ["--text-5xl", 3],
  ["--text-6xl", 3.75],
  ["--text-7xl", 4.5],
  ["--text-8xl", 6],
  ["--text-9xl", 8],
] as const;

/** Uniwind compiles `rem` to this many points on native (its default `polyfills.rem`). */
const NATIVE_REM_PX = 16;

/**
 * The `--text-*` overrides for `size`, for a Uniwind `ScopedVariables` around a slide body. Web
 * keeps `rem` so the browser's own zoom and root font size still apply; native needs plain point
 * values.
 */
export function getSlideTextSizeCSSVariables(
  size: SlideTextSize,
  platform: "native" | "web",
): Record<string, number | string> {
  const scale = SLIDE_TEXT_SIZE_SCALES[size];
  return Object.fromEntries(
    TAILWIND_TEXT_SIZES_REM.map(([variable, rem]) => [
      variable,
      platform === "web" ? `${rem * scale}rem` : rem * NATIVE_REM_PX * scale,
    ]),
  );
}
